/**
 * EmailIntentPromptHost — mounts the C3a "which inbox?" modal over ANY page
 * for the CHAT connect lane (C3b, 2026-08-21; same app-wide host pattern as
 * ComposioConnectCoachHost). The chat card's connect click asks over the
 * emailIntentChannel round-trip; this host renders the SAME EmailIntentPrompt
 * the Integrations panel uses — one component, one copy, both surfaces —
 * then delivers the answer back to the awaiting click handler.
 *
 * One prompt at a time: a second request while one is showing is answered
 * immediately with no intent (never queued — the user is mid-decision).
 */

import React, { useEffect, useState } from 'react';
import EmailIntentPrompt from '../services/composio/panel/EmailIntentPrompt';
import {
  EMAIL_INTENT_REQUEST_EVENT,
  ackEmailIntentRequest,
  respondEmailIntent,
  type EmailIntentRequestDetail,
} from '../services/composio/emailIntentChannel';

interface PromptState {
  requestId: number;
  toolkitSlug: string;
  displayName: string;
}

export const EmailIntentPromptHost: React.FC = () => {
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  useEffect(() => {
    const onRequest = (e: Event) => {
      const detail = (e as CustomEvent).detail as EmailIntentRequestDetail | undefined;
      if (!detail || typeof detail.requestId !== 'number' || !detail.toolkitSlug) return;
      setPrompt((current) => {
        if (current) {
          // Busy: answer the newcomer straight away with no intent so its
          // click proceeds un-prompted rather than waiting forever.
          respondEmailIntent({ requestId: detail.requestId, intendedAddress: null, cancelled: false });
          return current;
        }
        ackEmailIntentRequest(detail.requestId);
        return {
          requestId: detail.requestId,
          toolkitSlug: detail.toolkitSlug,
          displayName: detail.displayName || detail.toolkitSlug,
        };
      });
    };
    window.addEventListener(EMAIL_INTENT_REQUEST_EVENT, onRequest as EventListener);
    return () => window.removeEventListener(EMAIL_INTENT_REQUEST_EVENT, onRequest as EventListener);
  }, []);

  if (!prompt) return null;
  return (
    <EmailIntentPrompt
      slug={prompt.toolkitSlug}
      displayName={prompt.displayName}
      onContinue={(intendedAddress) => {
        respondEmailIntent({ requestId: prompt.requestId, intendedAddress, cancelled: false });
        setPrompt(null);
      }}
      onCancel={() => {
        respondEmailIntent({ requestId: prompt.requestId, intendedAddress: null, cancelled: true });
        setPrompt(null);
      }}
    />
  );
};

export default EmailIntentPromptHost;
